import React from 'react';

/**
 * Wraps the login screen with the context an invited visitor arrived with.
 *
 * Without this, following an invitation link lands a stranger on an
 * unexplained sign-in form — they were told a relative shared a family tree and
 * they are looking at a password box. Naming the tree and the person who sent it
 * is the whole job.
 *
 * The lookup can legitimately fail while the visitor is perfectly welcome (the
 * API is down, they are offline), so a missing preview renders nothing at all
 * and leaves the ordinary login screen in place. It never blocks sign-in.
 */

const ROLE_TEXT = {
  viewer: 'view',
  contributor: 'view and add to',
  admin: 'view and edit',
};

const shell = {
  padding: '14px 18px',
  borderRadius: 12,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13,
  lineHeight: 1.55,
  textAlign: 'center',
};

export default function InviteLanding({ invite, children }) {
  if (!invite) return <>{children}</>;

  let banner = null;

  if (invite.status === 'valid') {
    banner = (
      <div style={{ ...shell, background: '#F5EDE3', border: '1px solid #E8E0D4', color: '#2C2418' }}>
        <strong>{invite.inviterName}</strong> invited you to{' '}
        {ROLE_TEXT[invite.role] || 'view'} <strong>{invite.treeName}</strong>.
        <div style={{ marginTop: 6, fontSize: 12, color: '#6B5E4F' }}>
          Sign in or create an account below — any email address will do — and the
          tree will be waiting.
        </div>
      </div>
    );
  } else if (invite.status === 'used') {
    banner = (
      <div style={{ ...shell, background: '#FDF3E3', border: '1px solid #E8D9BE', color: '#6B5E4F' }}>
        This invitation link has already been used. If that wasn't you, ask
        whoever invited you to send a new one.
      </div>
    );
  } else if (invite.status === 'expired') {
    banner = (
      <div style={{ ...shell, background: '#FDF3E3', border: '1px solid #E8D9BE', color: '#6B5E4F' }}>
        This invitation link has expired. Ask whoever invited you to send a new
        one — they last two weeks.
      </div>
    );
  }

  if (!banner) return <>{children}</>;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
        display: 'flex', justifyContent: 'center', padding: '18px 16px 0',
        pointerEvents: 'none',
      }}>
        <div style={{ maxWidth: 420, width: '100%', pointerEvents: 'auto' }}>
          {banner}
        </div>
      </div>
      {children}
    </div>
  );
}
